import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Context from "../../Context";
import { useToast, Spinner } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  FiAlertTriangle,
  FiMaximize2,
  FiX,
  FiChevronLeft,
  FiSkipForward,
  FiCheck,
  FiInfo,
  FiClock,
  FiSearch,
} from "react-icons/fi";
import {
  FaTimes,
  FaCheckCircle,
  FaTrash,
  FaQrcode,
  FaRedo,
  FaSave,
  FaHistory,
  FaUserCheck,
} from "react-icons/fa";

const PAGE_SIZE = 100;

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (ts) => {
  if (!ts) return "-";
  return (
    new Date(Number(ts)).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }) + " น."
  );
};

const showToast = (toast, success, successMsg, failMsg) =>
  toast({
    className: success ? "bg-green text-white" : "bg-failed text-white",
    title: (
      <div className="flex gap-2 items-center text-sm font-semibold">
        {success ? <FaCheckCircle /> : <FaTimes />}
        {success ? successMsg : failMsg || "ผิดพลาด กรุณาลองใหม่อีกครั้ง"}
      </div>
    ),
  });

const THAI_TO_ENG_KEY_MAP = {
  // Kedmanee number row unshifted
  "ๅ": "1",
  "/": "2",
  "-": "3",
  "ภ": "4",
  "ถ": "5",
  "ุ": "6",
  "ึ": "7",
  "ค": "8",
  "ต": "9",
  "จ": "0",
  "ข": "-",
  "ช": "=",

  // Kedmanee number row shifted
  "+": "1",
  "๑": "1",
  "๒": "2",
  "๓": "3",
  "๔": "4",
  "ู": "6",
  "฿": "7",
  "๕": "8",
  "๖": "9",
  "๗": "0",
  "๘": "-",
  "๙": "=",
  "๐": "0",
};

/**
 * Parses and strictly validates Lottery QR Code:
 * 1. Checks and rejects 1D Barcode indicators.
 * 2. Normalizes Thai keyboard inputs and strips scanner AIM tags.
 * 3. Enforces the strict 16-digit Thai Lottery QR structure (YY-RR-SS-NNNNNN-BBBB).
 */
export const parseLotteryQrScan = (rawVal) => {
  if (!rawVal) return { valid: false, code: "", error: null };
  let str = String(rawVal).trim();

  // Reject 1D barcode scanner AIM prefixes explicitly
  if (/^\][CIEAe]/.test(str)) {
    return {
      valid: false,
      code: str,
      is1DBarcode: true,
      error: "ตรวจพบบาร์โค้ดแท่ง (1D) กรุณาสแกนเฉพาะ QR Code บนใบสลาก",
    };
  }

  // Strip scanner AIM ID prefix (]Q1, ]d1, etc.) or control characters
  // eslint-disable-next-line no-control-regex
  str = str.replace(/^\][a-zA-Z0-9]{2}/, "").replace(/[\x00-\x1F\x7F]/g, "");

  // Convert Thai keyboard characters if present
  if (/[ก-๙]/.test(str)) {
    let converted = "";
    for (const ch of str) {
      converted +=
        THAI_TO_ENG_KEY_MAP[ch] !== undefined ? THAI_TO_ENG_KEY_MAP[ch] : ch;
    }
    str = converted.trim();
  }

  // Reject concatenated / multiple scans (> 16 digits) before regex matching
  const digitsOnly = str.replace(/\D/g, "");
  if (digitsOnly.length > 16) {
    return {
      valid: false,
      code: str,
      isMultipleScan: true,
      error: `รหัสเกิน 16 หลัก (${digitsOnly.length}/16 — อาจเกิดจากการสแกนซ้อนกัน) กรุณากด Esc ล้างค่าแล้วสแกนใหม่`,
    };
  }

  // Standard 5-segment pattern YY-RR-SS-NUMBER-RRRR
  let normalized = "";
  const separatedMatch = str.match(
    /^(\d{2})[\s/_]+(\d{2})[\s/_]+(\d{2})[\s/_]+(\d{6})[\s/_]+(\d{4})$/
  );
  if (separatedMatch) {
    normalized = `${separatedMatch[1]}-${separatedMatch[2]}-${separatedMatch[3]}-${separatedMatch[4]}-${separatedMatch[5]}`;
  } else if (/^\d{2}-\d{2}-\d{2}-\d{6}-\d{4}$/.test(str)) {
    normalized = str;
  } else {
    const formatMatch = str.match(/(\d{2})-(\d{2})-(\d{2})-(\d{6})-(\d{4})/);
    if (formatMatch) {
      normalized = `${formatMatch[1]}-${formatMatch[2]}-${formatMatch[3]}-${formatMatch[4]}-${formatMatch[5]}`;
    } else {
      const continuousMatch = str.match(/\b\d{16}\b/);
      if (continuousMatch) {
        const d = continuousMatch[0];
        normalized = `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4, 6)}-${d.slice(6, 12)}-${d.slice(12, 16)}`;
      } else if (digitsOnly.length === 16) {
        normalized = `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2, 4)}-${digitsOnly.slice(4, 6)}-${digitsOnly.slice(6, 12)}-${digitsOnly.slice(12, 16)}`;
      }
    }
  }

  if (!normalized) {
    if (digitsOnly.length > 0 && digitsOnly.length < 16) {
      return {
        valid: false,
        code: str,
        is1DBarcode: true,
        error: `รหัสไม่ครบ 16 หลัก (${digitsOnly.length}/16) อาจเป็นบาร์โค้ดแท่ง กรุณาสแกน QR Code`,
      };
    }
    return {
      valid: false,
      code: str,
      error: "รูปแบบ QR Code ไม่ถูกต้อง (ต้องเป็น 16 หลัก: ปี-งวด-ชุด-เลขสลาก-เล่ม)",
    };
  }

  const parts = normalized.split("-");
  if (/^[23]/.test(parts[0])) {
    return {
      valid: false,
      code: normalized,
      is1DBarcode: true,
      error: "รหัสขึ้นต้นด้วย 2 หรือ 3 อาจเป็นบาร์โค้ด กรุณาสแกน QR Code บนใบสลาก",
    };
  }
  return {
    valid: true,
    code: normalized,
    lotteryId: `${parts[0]}-${parts[1]}-${parts[2]}-${parts[3]}`,
    segments: {
      year: parts[0],
      round: parts[1],
      set: parts[2],
      number: parts[3],
      book: parts[4],
    },
  };
};

// ── Main Component ────────────────────────────────────────────────────────────

const BadLotteries = () => {
  const { callApi, configs } = Context();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState("inspect"); // "inspect" | "history"

  // Inspection State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState("all");
  const [currentIndex, setCurrentIndex] = useState(0);

  // Scanning State for Current Ticket
  const [scanRawInput, setScanRawInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [sessionSavedMap, setSessionSavedMap] = useState({});

  // History State
  const [historyLogs, setHistoryLogs] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState("");

  const inputRef = useRef(null);
  const lastScanTimestampRef = useRef(0);
  const savingRef = useRef(false);
  const itemDisplayedAtRef = useRef(Date.now());

  // Load bad lotteries from API
  const getData = useCallback(async () => {
    if (!configs?.roundDate) return;
    setLoading(true);
    const data = await callApi(
      "bads-getAll",
      {
        queries: [["roundDate", "==", configs?.roundDate]],
        roundDate: configs?.roundDate,
        limit: PAGE_SIZE,
        orderBy: ["createdRawAt", "desc"],
      },
      { notShowLoading: true }
    );
    const list = data || [];
    setItems(list);
    setCurrentIndex(0);
    setScanRawInput("");
    if (!data) showToast(toast, false, null, "ดึงข้อมูลสลากเสียผิดพลาด");
    setLoading(false);
  }, [callApi, configs?.roundDate, toast]);

  // Load history logs from API
  const fetchHistory = useCallback(async () => {
    if (!configs?.roundDate) return;
    setLoadingHistory(true);
    const data = await callApi(
      "bads-getLogs",
      { roundDate: configs?.roundDate },
      { notShowLoading: true }
    );
    setHistoryLogs(data || []);
    setLoadingHistory(false);
  }, [callApi, configs?.roundDate]);

  useEffect(() => {
    if (configs?.roundDate) {
      getData();
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs?.roundDate]);

  useEffect(() => {
    if (activeTab === "history" && configs?.roundDate) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Unique books list for filtering
  const uniqueBooks = useMemo(() => {
    const set = new Set();
    items.forEach((x) => {
      if (x.fileNameId) set.add(x.fileNameId);
    });
    return Array.from(set).sort();
  }, [items]);

  // Filtered items based on selected book
  const filteredItems = useMemo(() => {
    if (selectedBook === "all") return items;
    return items.filter((x) => x.fileNameId === selectedBook);
  }, [items, selectedBook]);

  // Filtered history based on search term
  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return historyLogs;
    return historyLogs.filter(
      (log) =>
        (log.number || "").includes(q) ||
        (log.bookNo || "").includes(q) ||
        (log.fileNameId || "").toLowerCase().includes(q) ||
        (log.adminName || "").toLowerCase().includes(q) ||
        (log.matrixText || "").includes(q)
    );
  }, [historyLogs, historySearch]);

  // Clamp current index if filtered list shrinks
  useEffect(() => {
    if (currentIndex >= filteredItems.length && filteredItems.length > 0) {
      setCurrentIndex(filteredItems.length - 1);
      setScanRawInput("");
    }
  }, [filteredItems.length, currentIndex]);

  const currentItem = filteredItems[currentIndex] || null;

  // Reset image load readiness and timestamp whenever the active ticket changes
  useEffect(() => {
    setImageLoaded(!currentItem?.publicUrl);
    itemDisplayedAtRef.current = Date.now();
  }, [currentItem?.id, currentItem?.publicUrl]);

  // Real-time QR Parsing & Validation of current ticket's input
  const qrValidation = useMemo(() => {
    return parseLotteryQrScan(scanRawInput);
  }, [scanRawInput]);

  // Check if the scanned QR code was already saved in this session or in historyLogs
  const duplicateScanCheck = useMemo(() => {
    if (!qrValidation?.valid) return null;
    const { lotteryId, code, segments } = qrValidation;

    if (sessionSavedMap[lotteryId]) {
      return {
        isDuplicate: true,
        message: `สลากเลข ${segments.number} (ชุดที่ ${segments.set}) เพิ่งถูกบันทึกไปแล้ว ห้ามบันทึกซ้ำ!`,
      };
    }

    const foundInHistory = historyLogs.find((log) => {
      const m = String(log.matrixText || "");
      const parts = m.split("-");
      const logLotteryId =
        parts.length >= 4 ? `${parts[0]}-${parts[1]}-${parts[2]}-${parts[3]}` : m;
      return logLotteryId === lotteryId || m === code;
    });

    if (foundInHistory) {
      return {
        isDuplicate: true,
        message: `สลากเลข ${segments.number} (ชุดที่ ${segments.set}) เคยถูกบันทึกในประวัติสลากเสียแล้ว ห้ามบันทึกซ้ำ!`,
      };
    }

    return { isDuplicate: false, message: null };
  }, [qrValidation, sessionSavedMap, historyLogs]);

  // Auto-focus input whenever current item changes or on load
  const focusInput = useCallback(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 80);
  }, []);

  useEffect(() => {
    if (activeTab === "inspect") {
      focusInput();
    }
  }, [currentIndex, selectedBook, activeTab, focusInput]);

  // Handle Input Change (QR Gun Scan or Manual Entry)
  const handleInputChange = (e) => {
    if (saving || savingRef.current) return;
    const val = e.target.value;
    lastScanTimestampRef.current = Date.now();
    setScanRawInput(val);
  };

  // Cross-check book number, set number, and image sequence against item's fileNameId & path
  const bookCheck = useMemo(() => {
    if (!qrValidation?.valid || !currentItem) return null;
    const qrBook = qrValidation.segments.book;
    const qrSet = qrValidation.segments.set;
    const qrNumber = qrValidation.segments.number;
    const qrLastTwo = qrNumber.slice(-2);
    const fileNameId = String(currentItem.fileNameId || "").trim();
    const rawPath = decodeURIComponent(
      String(currentItem.path || currentItem.publicUrl || "")
    );

    const stdFileMatch = fileNameId.match(/^(\d{4})-(\d{2})-zip$/i);
    const seqMatch = rawPath.match(/0000-00-.*-(\d{4})\.jpg$/i);
    const imgSeq = seqMatch ? parseInt(seqMatch[1], 10) : null;
    const expectedLastTwo =
      imgSeq !== null && imgSeq >= 1 && imgSeq <= 100
        ? String(imgSeq - 1).padStart(2, "0")
        : null;

    if (stdFileMatch) {
      const [, expectedBook, expectedSet] = stdFileMatch;
      const bookMatched = qrBook === expectedBook;
      const setMatched = qrSet === expectedSet;
      const seqMatched =
        expectedLastTwo === null || expectedLastTwo === qrLastTwo;
      const isMatched = bookMatched && setMatched && seqMatched;

      const mismatchReasons = [];
      if (!bookMatched) {
        mismatchReasons.push(
          `เลขเล่มไม่ตรง (สแกนได้เล่ม ${qrBook} แต่ไฟล์คือเล่ม ${expectedBook})`
        );
      }
      if (!setMatched) {
        mismatchReasons.push(
          `เลขชุดไม่ตรง (สแกนได้ชุด ${qrSet} แต่ไฟล์คือชุด ${expectedSet})`
        );
      }
      if (!seqMatched) {
        mismatchReasons.push(
          `ลำดับไฟล์รูป (${String(imgSeq).padStart(4, "0")}.jpg มักลงท้าย ${expectedLastTwo}) ไม่ตรงกับเลขท้ายที่สแกน (${qrLastTwo})`
        );
      }

      return {
        isMatched,
        isStandardBookFile: true,
        qrBook,
        qrSet,
        fileNameId,
        mismatchReasons,
      };
    }

    // Non-standard file (e.g., E023-100-zip): check exact or substring match if numeric book
    const isMatched =
      fileNameId === qrBook ||
      fileNameId.includes(qrBook) ||
      qrBook.includes(fileNameId);

    return {
      isMatched,
      isStandardBookFile: false,
      qrBook,
      qrSet,
      fileNameId,
      mismatchReasons: isMatched
        ? []
        : [`QR Code สแกนได้เล่ม ${qrBook} แต่รูปมาจากไฟล์ ${fileNameId}`],
    };
  }, [qrValidation, currentItem]);

  // Confirm & Save current ticket
  const handleConfirmSave = async () => {
    if (
      !currentItem ||
      !qrValidation?.valid ||
      duplicateScanCheck?.isDuplicate ||
      saving ||
      savingRef.current
    ) {
      return;
    }

    if (currentItem.publicUrl && !imageLoaded) {
      showToast(
        toast,
        false,
        null,
        "กรุณารอให้รูปภาพสลากใบปัจจุบันโหลดเสร็จก่อนบันทึก"
      );
      return;
    }

    if (bookCheck && !bookCheck.isMatched) {
      const reasonText = (bookCheck.mismatchReasons || []).join("\n- ");
      const confirmed = window.confirm(
        `⚠️ พบข้อมูลไม่ตรงกับไฟล์รูปภาพ:\n- ${reasonText}\n\nกรุณาตรวจสอบตัวเลข 6 หลักบนรูปภาพให้แน่ใจว่าตรงกับเลข ${qrValidation.segments.number}\nยืนยันที่จะบันทึกสลากใบนี้หรือไม่?`
      );
      if (!confirmed) {
        focusInput();
        return;
      }
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const res = await callApi(
        "bads-edit",
        {
          id: currentItem.id,
          matrixText: qrValidation.code,
        },
        { notShowLoading: true }
      );

      if (res?.status) {
        setSessionSavedMap((prev) => ({
          ...prev,
          [qrValidation.lotteryId]: {
            code: qrValidation.code,
            number: qrValidation.segments.number,
            savedAt: Date.now(),
          },
        }));

        showToast(
          toast,
          true,
          `บันทึกสลากเลข ${qrValidation.segments.number} เรียบร้อยแล้ว`
        );

        // Remove saved item from local list
        setItems((prev) => prev.filter((x) => x.id !== currentItem.id));
        setScanRawInput("");
        focusInput();
        fetchHistory();
      } else {
        if (res?.isDuplicate) {
          setSessionSavedMap((prev) => ({
            ...prev,
            [qrValidation.lotteryId]: {
              code: qrValidation.code,
              number: qrValidation.segments.number,
              savedAt: Date.now(),
            },
          }));
        }
        showToast(toast, false, null, res?.toastMessage || "บันทึกไม่สำเร็จ");
      }
    } catch (err) {
      showToast(toast, false, null, "เกิดข้อผิดพลาดในการเชื่อมต่อ");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Delete current ticket
  const handleDeleteCurrent = async () => {
    if (!currentItem || deleting) return;
    if (
      !window.confirm(
        `ยืนยันลบสลากใบนี้ออกจากรายการสลากเสียหรือไม่? (สลากเล่ม ${currentItem.fileNameId})`
      )
    ) {
      return;
    }

    setDeleting(true);
    const res = await callApi(
      "bads-deletes",
      { id: currentItem.id },
      { notShowLoading: true }
    );
    setDeleting(false);

    if (res?.status) {
      showToast(toast, true, "ลบสลากออกจากระบบแล้ว");
      setItems((prev) => prev.filter((x) => x.id !== currentItem.id));
      setScanRawInput("");
      focusInput();
    } else {
      showToast(toast, false, null, res?.toastMessage || "ลบไม่สำเร็จ");
    }
  };

  // Skip / Next ticket
  const handleSkipNext = () => {
    if (filteredItems.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % filteredItems.length);
    setScanRawInput("");
    focusInput();
  };

  // Previous ticket
  const handlePrev = () => {
    if (filteredItems.length <= 1) return;
    setCurrentIndex((prev) =>
      prev === 0 ? filteredItems.length - 1 : prev - 1
    );
    setScanRawInput("");
    focusInput();
  };

  // Clear current scan
  const handleClearScan = () => {
    setScanRawInput("");
    focusInput();
  };

  // Handle Key Down on Input
  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const timeSinceScan = Date.now() - lastScanTimestampRef.current;
      const timeSinceItemShown = Date.now() - itemDisplayedAtRef.current;
      if (timeSinceScan < 350 || timeSinceItemShown < 400) {
        return;
      }
      if (
        qrValidation.valid &&
        !duplicateScanCheck?.isDuplicate &&
        !saving &&
        !savingRef.current
      ) {
        handleConfirmSave();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleClearScan();
    }
  };

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1440px] mx-auto pb-16 px-4">
      {/* ── Page Header ────────────────────────────────────────────── */}
      <Header
        header="Bad Lotteries"
        subHeader="ระบบตรวจสอบและแก้ไขสลากเสีย (ตรวจทีละใบ - สแกน QR Code)"
        icon={<FiAlertTriangle className="text-failed text-2xl" />}
      />

      {/* ── Top Tabs: Inspect Mode vs History Mode ─────────────────── */}
      <div className="flex items-center gap-2 mt-5">
        <button
          type="button"
          onClick={() => setActiveTab("inspect")}
          className={`flex items-center gap-2 py-2 px-4 rounded-md text-xs font-bold transition shadow-2xs cursor-pointer ${
            activeTab === "inspect"
              ? "bg-navy text-white shadow-xs"
              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          <FaQrcode className="text-sm" />
          <span>ตรวจสลากเสีย ({filteredItems.length} ใบ)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("history")}
          className={`flex items-center gap-2 py-2 px-4 rounded-md text-xs font-bold transition shadow-2xs cursor-pointer ${
            activeTab === "history"
              ? "bg-navy text-white shadow-xs"
              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          <FaHistory className="text-sm" />
          <span>
            ประวัติที่แก้ไขแล้ว
            {historyLogs.length > 0 && ` (${historyLogs.length})`}
          </span>
        </button>
      </div>

      {/* ── TAB 1: INSPECTION MODE ─────────────────────────────────── */}
      {activeTab === "inspect" && (
        <>
          {/* Top Control Bar: Book Filter & Progress */}
          <div className="mt-4 mb-5 bg-white border border-[#E5E7E9] rounded-md shadow-xs p-4 flex flex-wrap items-center justify-between gap-4">
            {/* Left: Book Filter Dropdown */}
            <div className="flex items-center gap-3">
              <label
                htmlFor="book-select"
                className="text-xs font-bold text-gray-700 flex items-center gap-1.5"
              >
                <span>กรองตามเล่ม (ไฟล์):</span>
              </label>
              <select
                id="book-select"
                value={selectedBook}
                onChange={(e) => {
                  setSelectedBook(e.target.value);
                  setCurrentIndex(0);
                  setScanRawInput("");
                }}
                className="border border-gray-300 focus:border-navy text-xs font-bold text-gray-800 bg-white rounded-md px-3 py-2 outline-none shadow-xs"
              >
                <option value="all">ทุกเล่ม ({items.length} ใบ)</option>
                {uniqueBooks.map((b) => (
                  <option key={b} value={b}>
                    เล่ม {b} ({items.filter((x) => x.fileNameId === b).length} ใบ)
                  </option>
                ))}
              </select>

              <button
                onClick={getData}
                disabled={loading}
                className="p-2 text-gray-500 hover:text-navy hover:bg-gray-100 rounded-md transition cursor-pointer"
                title="รีเฟรชข้อมูล"
              >
                <FaRedo className={`text-xs ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>

            {/* Right: Inspection Progress Counter */}
            <div className="flex items-center gap-4">
              <div className="text-xs font-semibold text-gray-600 flex items-center gap-2">
                <span>งวด:</span>
                <span className="font-bold text-navy font-mono bg-blue/10 px-2 py-0.5 rounded-md">
                  {configs?.roundDate || "-"}
                </span>
              </div>

              <div className="flex items-center gap-2 bg-slate-100 text-gray-700 rounded-md px-3 py-1.5 text-xs font-bold font-mono">
                <span>คงเหลือ:</span>
                <span className="text-sm font-black text-rose-600">
                  {filteredItems.length}
                </span>
                <span className="text-[11px] font-normal text-gray-500">ใบ</span>
              </div>
            </div>
          </div>

          {/* Main Inspection Body */}
          {loading ? (
            <div className="py-28 flex flex-col justify-center items-center bg-white rounded-md border border-gray-200 shadow-xs">
              <Spinner className="w-9 h-9" color="border-navy" />
              <span className="mt-3 text-xs font-bold text-navy">
                กำลังโหลดข้อมูลสลากเสีย...
              </span>
            </div>
          ) : filteredItems.length === 0 ? (
            /* Empty State */
            <div className="py-24 flex flex-col justify-center items-center bg-white rounded-md border border-[#E5E7E9] shadow-xs text-center p-6 gap-3">
              <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl shadow-inner">
                <FaCheckCircle />
              </div>
              <h3 className="text-lg font-bold text-gray-800">
                ไม่มีสลากที่มีปัญหาในงวดนี้
              </h3>
              <p className="text-xs font-medium text-gray-500 max-w-md">
                สลากทั้งหมดได้รับการสแกนและนำเข้าสู่ระบบเรียบร้อยแล้ว
                {selectedBook !== "all" &&
                  " (หรือไม่มีรายการในเล่มที่เลือก ลองเปลี่ยนตัวกรองเป็น 'ทุกเล่ม')"}
              </p>
              {selectedBook !== "all" && (
                <button
                  onClick={() => setSelectedBook("all")}
                  className="mt-2 text-xs font-bold text-navy bg-navy/10 hover:bg-navy/20 py-2 px-4 rounded-md transition cursor-pointer"
                >
                  แสดงทุกเล่ม
                </button>
              )}
            </div>
          ) : (
            /* Single Ticket Inspection Box */
            <div className="bg-white border border-[#E5E7E9] rounded-md shadow-sm overflow-hidden">
              {/* Progress Header Bar */}
              <div className="bg-slate-50 border-b border-gray-200 px-5 py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="bg-navy text-white text-xs font-bold px-3 py-1 rounded-md font-mono shadow-2xs">
                    ใบที่ {currentIndex + 1} / {filteredItems.length}
                  </span>
                  <span className="text-xs font-bold text-gray-700">
                    เล่ม:{" "}
                    <span className="font-mono text-navy font-black text-sm">
                      {currentItem.fileNameId}
                    </span>
                  </span>
                  <span className="text-gray-300">|</span>
                  <span className="text-xs text-gray-500 font-medium">
                    นำเข้าเมื่อ: {formatDate(currentItem.createdRawAt)}
                  </span>
                </div>

                {/* Navigation Buttons (Prev / Skip) */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handlePrev}
                    disabled={filteredItems.length <= 1}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-navy hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent py-1.5 px-3 rounded-md transition cursor-pointer"
                    title="ย้อนกลับใบก่อนหน้า"
                  >
                    <FiChevronLeft className="text-sm" />
                    <span>ก่อนหน้า</span>
                  </button>
                  <button
                    onClick={handleSkipNext}
                    disabled={filteredItems.length <= 1}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-navy hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent py-1.5 px-3 rounded-md transition cursor-pointer"
                    title="ข้ามไปใบถัดไป"
                  >
                    <span>ข้ามใบนี้</span>
                    <FiSkipForward className="text-sm" />
                  </button>
                </div>
              </div>

              {/* 2-Column Split: Image on Left (60%), Verification on Right (40%) */}
              <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* ── Left Column: Large Ticket Image ── */}
                <div className="lg:col-span-7 flex flex-col gap-3">
                  <div className="relative group w-full h-[380px] sm:h-[460px] bg-slate-900/5 rounded-md border-2 border-gray-200 overflow-hidden flex items-center justify-center shadow-inner">
                    {currentItem.publicUrl ? (
                      <>
                        {!imageLoaded && (
                          <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center gap-2">
                            <Spinner className="w-7 h-7" color="border-navy" />
                            <span className="text-xs font-bold text-navy">
                              กำลังโหลดรูปสลากใบถัดไป...
                            </span>
                          </div>
                        )}
                        <img
                          key={currentItem.id || currentItem.publicUrl}
                          src={currentItem.publicUrl}
                          alt="bad lottery ticket"
                          onLoad={() => setImageLoaded(true)}
                          onError={() => setImageLoaded(true)}
                          className="w-full h-full object-contain p-2 transition-transform duration-200 group-hover:scale-105 cursor-pointer select-none"
                          onClick={() => setPreviewImageUrl(currentItem.publicUrl)}
                        />
                        <button
                          type="button"
                          onClick={() => setPreviewImageUrl(currentItem.publicUrl)}
                          className="absolute top-3 right-3 bg-black/70 hover:bg-black text-white px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition duration-150 cursor-pointer text-xs font-semibold flex items-center gap-1.5 shadow-md"
                        >
                          <FiMaximize2 className="text-sm" />
                          <span>ขยายดูเต็มจอ</span>
                        </button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-400 font-semibold text-xs">
                        <FiAlertTriangle className="text-3xl text-gray-300" />
                        <span>ไม่พบไฟล์รูปภาพสลาก</span>
                      </div>
                    )}
                  </div>

                  {/* Tips for Admin */}
                  <div className="bg-blue/5 border border-blue/15 rounded-md p-3 flex items-start gap-2 text-xs text-slate-600">
                    <FiInfo className="text-navy text-sm shrink-0 mt-0.5" />
                    <span>
                      ตรวจสอบตัวเลข 6 หลักบนรูปสลาก เทียบกับตัวเลขที่ได้จากการสแกน QR
                      Code ทางขวามือให้ตรงกันก่อนกดบันทึก
                    </span>
                  </div>
                </div>

                {/* ── Right Column: QR Scanner & Verification Panel ── */}
                <div className="lg:col-span-5 flex flex-col gap-5">
                  {/* QR Scanner Input Zone */}
                  <div className="bg-slate-50 border border-gray-200 rounded-md p-4 shadow-2xs">
                    <label
                      htmlFor="qr-scan-input"
                      className="text-xs font-bold text-gray-800 flex items-center justify-between mb-2"
                    >
                      <span className="flex items-center gap-1.5 text-navy">
                        <FaQrcode className="text-base" />
                        สแกน QR Code ใบสลาก
                      </span>
                      <span className="text-[11px] font-normal text-gray-400">
                        Auto-focus พร้อมยิง
                      </span>
                    </label>

                    <div className="relative">
                      <input
                        ref={inputRef}
                        id="qr-scan-input"
                        type="text"
                        autoComplete="off"
                        disabled={saving}
                        value={scanRawInput}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        placeholder="ยิง QR Code หรือพิมพ์รหัส 16 หลัก..."
                        className={`w-full font-mono font-bold text-sm px-3.5 py-3 pr-10 border-2 rounded-md transition shadow-2xs outline-none disabled:opacity-60 disabled:cursor-not-allowed ${
                          duplicateScanCheck?.isDuplicate
                            ? "border-rose-500 bg-rose-50/30 text-rose-900 focus:ring-2 focus:ring-rose-200"
                            : qrValidation.valid
                            ? "border-emerald-500 bg-emerald-50/20 text-emerald-900 focus:ring-2 focus:ring-emerald-200"
                            : qrValidation.is1DBarcode || qrValidation.error
                            ? "border-rose-400 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-200"
                            : "border-gray-300 bg-white text-gray-800 focus:border-navy focus:ring-2 focus:ring-navy/15"
                        }`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        {qrValidation.valid && !duplicateScanCheck?.isDuplicate ? (
                          <FaCheckCircle className="text-emerald-600 text-base" />
                        ) : (
                          <FaQrcode className="text-gray-400 text-base" />
                        )}
                      </div>
                    </div>

                    {/* Validation / 1D Barcode Rejection Alert */}
                    {qrValidation.error && (
                      <div className="mt-2.5 p-2.5 bg-rose-50 border border-rose-200 rounded-md flex items-start gap-2 text-xs font-bold text-rose-700 animate-in fade-in duration-150">
                        <FiAlertTriangle className="text-rose-600 text-sm shrink-0 mt-0.5" />
                        <span>{qrValidation.error}</span>
                      </div>
                    )}

                    {/* Duplicate Scan Rejection Alert */}
                    {duplicateScanCheck?.isDuplicate && (
                      <div className="mt-2.5 p-3 bg-rose-100 border-2 border-rose-500 rounded-md flex items-start gap-2 text-xs font-bold text-rose-900 animate-pulse">
                        <FiAlertTriangle className="text-rose-600 text-base shrink-0 mt-0.5" />
                        <div>
                          <span className="block">🚫 พบการสแกนรหัสสลากซ้ำ!</span>
                          <span className="font-normal text-[11px] block mt-0.5">
                            {duplicateScanCheck.message} กรุณากด Esc ล้างค่าแล้วหยิบสลากใบที่ตรงกับรูปภาพมาสแกนใหม่
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="mt-2 text-[11px] text-gray-400 flex items-center justify-between">
                      <span>* จำกัดเฉพาะ QR Code (ไม่รับบาร์โค้ดแท่ง)</span>
                      {scanRawInput && (
                        <button
                          onClick={handleClearScan}
                          className="text-gray-500 hover:text-rose-600 underline cursor-pointer"
                        >
                          ล้างค่า (Esc)
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Scanned Verification Box */}
                  {qrValidation.valid && qrValidation.segments ? (
                    <div className="bg-emerald-50/40 border-2 border-emerald-500 rounded-md p-4.5 flex flex-col gap-3.5 animate-in fade-in duration-200">
                      <div className="flex items-center justify-between border-b border-emerald-200 pb-2">
                        <span className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                          <FaCheckCircle className="text-emerald-600 text-sm" />
                          ผลการสแกน QR Code (โปรดตรวจกับรูป)
                        </span>
                        <span className="text-[11px] font-mono text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md font-semibold">
                          16 หลักครบถ้วน
                        </span>
                      </div>

                      {/* Super Large 6-Digit Number */}
                      <div className="flex flex-col items-center justify-center bg-white border border-emerald-300 rounded-md py-4 px-3 shadow-inner">
                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          เลขสลากกินแบ่งรัฐบาล
                        </span>
                        <span className="text-4xl sm:text-5xl font-mono font-black tracking-widest text-emerald-800">
                          {qrValidation.segments.number}
                        </span>
                      </div>

                      {/* Badges breakdown: Round / Set / Book / Year */}
                      <div className="grid grid-cols-4 gap-2 text-center text-xs">
                        <div className="bg-white border border-emerald-200 rounded-md p-2">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            ปี พ.ศ.
                          </span>
                          <span className="font-bold text-gray-800 font-mono">
                            {qrValidation.segments.year}
                          </span>
                        </div>
                        <div className="bg-white border border-emerald-200 rounded-md p-2">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            งวดที่
                          </span>
                          <span className="font-bold text-gray-800 font-mono">
                            {qrValidation.segments.round}
                          </span>
                        </div>
                        <div className="bg-white border border-emerald-200 rounded-md p-2">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            ชุดที่
                          </span>
                          <span className="font-bold text-gray-800 font-mono">
                            {qrValidation.segments.set}
                          </span>
                        </div>
                        <div className="bg-white border border-emerald-200 rounded-md p-2">
                          <span className="block text-[10px] text-gray-400 font-semibold">
                            เล่มที่
                          </span>
                          <span className="font-bold text-emerald-900 font-mono">
                            {qrValidation.segments.book}
                          </span>
                        </div>
                      </div>

                      {/* Book / Set / Sequence Mismatch Cross-check Alert */}
                      {bookCheck && (
                        <div
                          className={`p-3 rounded-md flex items-start gap-2 text-xs font-bold ${
                            bookCheck.isMatched
                              ? "bg-emerald-100/80 text-emerald-900 border border-emerald-300"
                              : "bg-rose-100 text-rose-900 border-2 border-rose-400 animate-pulse"
                          }`}
                        >
                          {bookCheck.isMatched ? (
                            <>
                              <FiCheck className="text-emerald-700 text-base shrink-0 mt-0.5" />
                              <span>
                                ข้อมูลเล่ม/ชุดตรงกับไฟล์รูป (เล่ม {bookCheck.qrBook} ชุด {bookCheck.qrSet})
                              </span>
                            </>
                          ) : (
                            <>
                              <FiAlertTriangle className="text-rose-600 text-base shrink-0 mt-0.5" />
                              <div>
                                <span className="block">
                                  ⚠️ ระวัง! ข้อมูลที่สแกนไม่ตรงกับไฟล์รูปภาพ
                                </span>
                                <ul className="font-normal text-[11px] list-disc pl-4 mt-1 space-y-0.5">
                                  {(bookCheck.mismatchReasons || []).map(
                                    (reason, idx) => (
                                      <li key={idx}>{reason}</li>
                                    )
                                  )}
                                </ul>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Placeholder waiting for scan */
                    <div className="border-2 border-dashed border-gray-200 rounded-md p-8 flex flex-col items-center justify-center text-center gap-2 text-gray-400">
                      <FaQrcode className="text-4xl text-gray-300" />
                      <span className="text-xs font-bold text-gray-500">
                        รอสแกน QR Code บนใบสลาก
                      </span>
                      <span className="text-[11px] text-gray-400 max-w-xs">
                        เมื่อสแกนแล้ว ตัวเลข 6 หลักและข้อมูลเล่มจะปรากฏที่นี่
                        เพื่อให้แอดมินตรวจสอบก่อนกดบันทึก
                      </span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2.5 pt-2">
                    {/* Primary Confirm & Save Button */}
                    <button
                      type="button"
                      onClick={handleConfirmSave}
                      disabled={
                        !qrValidation.valid ||
                        duplicateScanCheck?.isDuplicate ||
                        saving ||
                        (Boolean(currentItem.publicUrl) && !imageLoaded)
                      }
                      className="w-full bg-navy hover:bg-navy/90 text-white font-bold py-3 px-5 rounded-md text-sm shadow-md transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {saving ? (
                        <>
                          <Spinner className="w-4 h-4" color="border-white" />
                          <span>กำลังบันทึกเข้าสู่ระบบ...</span>
                        </>
                      ) : (
                        <>
                          <FaSave className="text-base" />
                          <span>
                            ยืนยันบันทึกและไปใบถัดไป{" "}
                            {qrValidation.valid && !duplicateScanCheck?.isDuplicate
                              ? "(กด Enter ได้)"
                              : ""}
                          </span>
                        </>
                      )}
                    </button>

                    {/* Sub-actions: Clear / Delete */}
                    <div className="flex items-center justify-between gap-3 pt-1">
                      <button
                        type="button"
                        onClick={handleClearScan}
                        disabled={!scanRawInput}
                        className="text-xs font-semibold text-gray-600 hover:text-gray-800 disabled:opacity-30 py-1.5 px-3 rounded-md hover:bg-gray-100 transition cursor-pointer"
                      >
                        ล้างค่าที่สแกน
                      </button>

                      <button
                        type="button"
                        onClick={handleDeleteCurrent}
                        disabled={deleting || saving}
                        className="flex items-center gap-1.5 text-xs font-semibold text-failed hover:bg-failed/10 py-1.5 px-3 rounded-md transition disabled:opacity-30 cursor-pointer"
                        title="ลบสลากใบนี้ออกจากระบบ"
                      >
                        {deleting ? (
                          <Spinner className="w-3.5 h-3.5" color="border-failed" />
                        ) : (
                          <FaTrash className="text-xs" />
                        )}
                        <span>ลบสลากใบนี้</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── TAB 2: AUDIT & HISTORY LOGS ─────────────────────────────── */}
      {activeTab === "history" && (
        <div className="mt-4 bg-white border border-[#E5E7E9] rounded-md shadow-sm overflow-hidden p-5 flex flex-col gap-4">
          {/* Header of History */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-navy/10 text-navy flex items-center justify-center font-bold">
                <FiClock className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-800">
                  ประวัติการแก้ไขสลากเสีย (งวด {configs?.roundDate || "-"})
                </h3>
                <p className="text-xs text-gray-500 font-medium">
                  บันทึกการแก้ไขสลากที่อ่าน QR Code สำเร็จ พร้อมระบุชื่อผู้ดำเนินการ
                </p>
              </div>
            </div>

            {/* Search Filter & Refresh */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="ค้นหาเลขสลาก หรือชื่อแอดมิน..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-xs font-semibold focus:border-navy focus:outline-none w-60 shadow-2xs"
                />
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              </div>

              <button
                type="button"
                onClick={fetchHistory}
                disabled={loadingHistory}
                className="flex items-center gap-1.5 py-1.5 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs font-bold transition cursor-pointer"
                title="รีเฟรชประวัติ"
              >
                <FaRedo className={`text-xs ${loadingHistory ? "animate-spin" : ""}`} />
                <span>รีเฟรช</span>
              </button>
            </div>
          </div>

          {/* History Content */}
          {loadingHistory ? (
            <div className="py-20 flex flex-col justify-center items-center">
              <Spinner className="w-7 h-7" color="border-navy" />
              <span className="mt-2 text-xs font-bold text-navy">
                กำลังโหลดประวัติ...
              </span>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="py-16 text-center text-gray-400 font-semibold text-xs flex flex-col items-center gap-2">
              <FiClock className="w-8 h-8 text-gray-300" />
              <span>
                {historySearch
                  ? "ไม่พบประวัติที่ตรงกับคำค้นหา"
                  : "ยังไม่มีประวัติการแก้ไขสลากเสียในงวดนี้"}
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-gray-200 text-gray-600 font-bold">
                    <th className="py-3 px-3 w-12 text-center">#</th>
                    <th className="py-3 px-3 w-36">วัน-เวลาที่แก้ไข</th>
                    <th className="py-3 px-3 w-24 text-center">รูปสลาก</th>
                    <th className="py-3 px-3">เลขสลาก</th>
                    <th className="py-3 px-3">เล่ม</th>
                    <th className="py-3 px-3">รหัสเต็ม (QR Code)</th>
                    <th className="py-3 px-3">ผู้แก้ไข</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-semibold text-gray-700">
                  {filteredHistory.map((log, index) => (
                    <tr
                      key={log.id || index}
                      className="hover:bg-slate-50/80 transition"
                    >
                      <td className="py-2.5 px-3 text-center text-gray-400 font-mono text-xs">
                        {index + 1}
                      </td>
                      <td className="py-2.5 px-3 text-gray-500 font-mono text-[11px]">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {log.publicUrl ? (
                          <img
                            src={log.publicUrl}
                            alt="lottery"
                            className="w-12 h-7 object-cover rounded-sm border border-gray-200 mx-auto cursor-pointer hover:opacity-85 shadow-2xs"
                            onClick={() => setPreviewImageUrl(log.publicUrl)}
                          />
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="font-mono font-black text-sm text-navy bg-navy/5 px-2 py-0.5 rounded-md">
                          {log.number || "-"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-mono">
                        {log.bookNo || log.fileNameId || "-"}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-[11px] text-gray-500">
                        {log.matrixText || "-"}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-md font-bold text-xs shadow-2xs">
                          <FaUserCheck className="text-emerald-600 text-xs" />
                          <span>{log.adminName || "แอดมิน"}</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Image Lightbox Modal ─────────────────────────────────────── */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setPreviewImageUrl(null)}
        >
          <div
            className="relative max-w-5xl max-h-[92vh] bg-white rounded-md overflow-hidden shadow-2xl p-2.5 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImageUrl(null)}
              className="absolute top-3 right-3 bg-black/60 hover:bg-black text-white p-2 rounded-full transition duration-150 cursor-pointer"
              title="ปิด"
            >
              <FiX className="w-5 h-5" />
            </button>
            <img
              src={previewImageUrl}
              alt="full lottery preview"
              className="max-h-[85vh] w-auto object-contain rounded-md"
            />
            <p className="text-xs font-semibold text-gray-500 mt-2">
              คลิกด้านนอกหรือกดปุ่มปิดเพื่อย้อนกลับ
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadLotteries;
